// Anmeldung vor dem Start.
//  · SRS-Konto: E-Mail + Passwort bei Smart Royalty Systems, dann 6-stelliger Code per Mail.
//    Der Browser meldet sich direkt bei SRS an; der Studio-Server prüft die SRS-Sitzung
//    und übernimmt Name, Rolle und Unternehmen. Das Studio speichert keine Passwörter.
//  · Name + Zugangscode: nur für Entwicklung und geschlossene Pilot-Camps.
import { useEffect, useState } from 'react';
import { useStudio } from '../state/store.ts';
import { api } from '../api/client.ts';
import { setSrsBasis, srsLogin, srsCode, istTestsystem } from '../srs/srs.ts';
import { toast } from '../components/ui.tsx';

const merk = { get: (k: string) => { try { return localStorage.getItem(k) ?? ''; } catch { return ''; } }, set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* privat */ } } };

export function Login() {
  const [art, setArt] = useState<{ srs: boolean; name: boolean; code: boolean } | null>(null);
  const [modus, setModus] = useState<'srs' | 'name'>('srs');
  useEffect(() => {
    api.health().then((h) => {
      const l = h.login ?? { srs: false, name: true, srsApi: '' };
      if (l.srsApi) setSrsBasis(l.srsApi);
      setArt({ srs: l.srs, name: l.name, code: !!h.accessCode });
      setModus(l.srs ? 'srs' : 'name');
    }).catch(() => setArt({ srs: false, name: true, code: false }));
  }, []);

  if (!art) return <div className="login"><div className="card"><div className="t2">Verbinde mit dem Server …</div></div></div>;
  return (
    <div className="login">
      <div className="card">
        {modus === 'srs' ? <SrsLogin /> : <NameLogin code={art.code} />}
        {art.srs && art.name && (
          <button className="btn small" style={{ alignSelf: 'center', border: 0 }} onClick={() => setModus(modus === 'srs' ? 'name' : 'srs')}>
            {modus === 'srs' ? 'Ohne SRS-Konto (Pilot-Camp mit Zugangscode)' : 'Mit SRS-Konto anmelden'}
          </button>
        )}
      </div>
    </div>
  );
}

function SrsLogin() {
  const [email, setEmail] = useState(merk.get('srs.email'));
  const [passwort, setPasswort] = useState('');
  const [vorgang, setVorgang] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');

  const schritt1 = async () => {
    setBusy(true); setFehler('');
    try {
      const r = await srsLogin(email.trim(), passwort);
      merk.set('srs.email', email.trim());
      setVorgang(r.vorgang); setPasswort('');
    } catch (e) { setFehler((e as Error).message); }
    finally { setBusy(false); }
  };
  const schritt2 = async () => {
    setBusy(true); setFehler('');
    try {
      const r = await srsCode(vorgang!, code);
      const s = await api.srsAuth(r.token);
      useStudio.getState().setAuth(s.token, s.user, r.token);
      useStudio.getState().setView('camp');
      toast(`Angemeldet als ${s.user.name}${s.user.mandant ? `, ${s.user.mandant}` : ''}`);
    } catch (e) {
      const m = (e as Error).message;
      setFehler(m);
      if (/abgelaufen|erneut anmelden/i.test(m)) { setVorgang(null); setCode(''); }
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="h1" style={{ fontSize: 24 }}>Anmelden</div>
      <div className="t2">Mit deinem Smart-Royalty-Systems-Konto. {istTestsystem() && <span className="pill amber">Testsystem</span>}</div>
      {!vorgang ? (
        <>
          <div className="field"><label>E-Mail</label><input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void schritt1()} /></div>
          <div className="field"><label>Passwort</label><input type="password" autoComplete="current-password" value={passwort} onChange={(e) => setPasswort(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void schritt1()} /></div>
          <button className="btn primary" disabled={busy || !email || !passwort} onClick={() => void schritt1()}>{busy ? 'Prüfe …' : 'Weiter'}</button>
        </>
      ) : (
        <>
          <div className="card wash t2" style={{ fontSize: 12.5 }}>Wir haben dir einen 6-stelligen Code an {email} geschickt. Er gilt 10 Minuten.</div>
          <div className="field"><label>Code aus der Mail</label><input inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} autoFocus onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, ''))} onKeyDown={(e) => e.key === 'Enter' && void schritt2()} style={{ fontFamily: 'var(--mono)', fontSize: 20, letterSpacing: '.3em' }} /></div>
          <button className="btn primary" disabled={busy || code.replace(/\s/g, '').length !== 6} onClick={() => void schritt2()}>{busy ? 'Melde an …' : 'Anmelden'}</button>
          <button className="btn small" style={{ border: 0, alignSelf: 'flex-start' }} onClick={() => { setVorgang(null); setCode(''); }}>Zurück</button>
        </>
      )}
      {fehler && <div className="pill red" style={{ whiteSpace: 'normal' }}>{fehler}</div>}
    </>
  );
}

function NameLogin({ code: brauchtCode }: { code: boolean }) {
  const [name, setName] = useState(merk.get('srs.name'));
  const [city, setCity] = useState(merk.get('srs.city'));
  const [code, setCode] = useState('');
  const login = async () => {
    try {
      const r = await api.devLogin(name || 'Gast', 'producer', city || undefined, code || undefined);
      useStudio.getState().setAuth(r.token, r.user);
      useStudio.getState().setView('camp');
      merk.set('srs.name', name); merk.set('srs.city', city);
      toast(`Angemeldet als ${r.user.name}`);
    } catch (e) { toast((e as Error).message === 'Zugangscode falsch' ? 'Zugangscode falsch' : 'Anmeldung nicht möglich'); }
  };
  return (
    <>
      <div className="h1" style={{ fontSize: 24 }}>Ins Camp</div>
      <div className="t2">Anmeldung per Name{brauchtCode ? ' und Zugangscode' : ''}, für Pilot-Camps.</div>
      <div className="field"><label>Name</label><input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void login()} /></div>
      <div className="field"><label>Stadt</label><input value={city} onChange={(e) => setCity(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void login()} /></div>
      {brauchtCode && <div className="field"><label>Zugangscode fürs Camp</label><input type="password" value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void login()} /></div>}
      <button className="btn primary" onClick={() => void login()}>Anmelden</button>
    </>
  );
}
